import {
  deliveryTitle,
  fmtSize,
  roDate,
  toConfirm,
  type Delivery,
  type DeliveryInput,
  type DeliveryKind,
  type PurchaseOrder,
  type Rfq,
  t,
} from '@procumi/shared';
import { useState, type ReactNode } from 'react';
import type { Toast } from '../hooks/useToast';
import { api, errMsg } from '../lib/api';
import { ask } from '../lib/confirm';
import { confirmDiscard, Dialog } from './Dialog';
import { DateInput } from './DateInput';

/** Ce se poate urca la un aviz: poza de pe telefon sau PDF-ul primit. */
const ACCEPT = 'application/pdf,image/png,image/jpeg,image/webp';
/** Ca pe server (multer): peste, fișierul nu mai pleacă degeaba. */
const MAX_MB = 20;

/** Azi, 'AAAA-LL-ZZ', după ceasul de aici. */
const today = (): string => {
  const d = new Date();
  const two = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${two(d.getMonth() + 1)}-${two(d.getDate())}`;
};

/**
 * Drumul spre server al livrărilor: din pagina comenzii (Achizițiile) sau din
 * „Livrări” (șantierul). `R` e ce întoarce serverul după fiecare pas.
 */
export interface DeliveryPort<R> {
  create: (body: DeliveryInput) => Promise<R>;
  update: (id: string, body: DeliveryInput) => Promise<R>;
  remove: (id: string) => Promise<R>;
  upload: (id: string, file: File) => Promise<R>;
  removeFile: (id: string, fileId: string) => Promise<R>;
  fileUrl: (id: string, fileId: string) => string;
  /** livrările comenzii din răspuns: așa se află livrarea nou creată */
  deliveriesOf: (r: R) => Delivery[];
}

/** Pagina comenzii: răspunsul e cererea întreagă. */
function orderPort(rfqId: string, orderId: string): DeliveryPort<Rfq> {
  return {
    create: (body) => api.createDelivery(rfqId, orderId, body),
    update: (id, body) => api.updateDelivery(rfqId, orderId, id, body),
    remove: (id) => api.deleteDelivery(rfqId, orderId, id),
    upload: (id, file) => api.uploadDeliveryFile(rfqId, orderId, id, file),
    removeFile: (id, fileId) => api.deleteDeliveryFile(rfqId, orderId, id, fileId),
    fileUrl: (id, fileId) => api.deliveryFileUrl(rfqId, orderId, id, fileId),
    deliveriesOf: (r) => r.orders?.find((o) => o.id === orderId)?.deliveries ?? [],
  };
}

/** Întrebarea dinaintea ștergerii; true = da. */
export const askRemove = (d: Delivery): Promise<boolean> =>
  ask({
    title: t('Ștergi livrarea?'),
    message: t(
      '{0} din {1}{2}. Nu se mai poate recupera.',
      deliveryTitle(d),
      roDate(d.date),
      d.files.length
        ? t(', cu {0}', d.files.length === 1 ? t('fișierul ei') : t('cele {0} fișiere', d.files.length))
        : '',
    ),
    confirm: t('Șterge livrarea'),
    danger: true,
  });

/** „Trecută de Mihai de pe șantier”, „confirmată de Elena”; nimic când a trecut-o chiar cine a confirmat. */
function whoLine(d: Delivery): string {
  const by = d.createdBy?.name ?? 'cineva';
  if (toConfirm(d)) return t('Trecută de {0} de pe șantier · de confirmat', by);
  if (d.createdBy && d.confirmedBy && d.createdBy.id !== d.confirmedBy.id) {
    return t('Trecută de {0} · confirmată de {1}', by, d.confirmedBy.name);
  }
  return '';
}

/** Un rând din lista de livrări: data, avizul, observațiile, fișierele, apoi butoanele. */
export function DeliveryItem({
  d,
  fileUrl,
  status,
  children,
}: {
  d: Delivery;
  fileUrl: (fileId: string) => string;
  /** rândul de sub titlu; implicit cine a trecut-o și dacă e confirmată */
  status?: string;
  children?: ReactNode;
}) {
  const line = status ?? whoLine(d);
  return (
    <li
      className={
        [d.kind === 'return' ? 'ret' : '', toConfirm(d) ? 'check' : ''].join(' ').trim() || undefined
      }
    >
      <span className="dlv-ico" aria-hidden="true">
        <svg width="15" height="15" viewBox="0 0 16 16">
          <path
            d={
              d.kind === 'return'
                ? 'M6 4 2.5 7.5 6 11M2.5 7.5h8a3 3 0 0 1 0 6H9'
                : 'M1.5 4h8v7h-8zM9.5 6.5h3l2 2.5v2h-5M4.5 13a1.3 1.3 0 1 0 0-2.6 1.3 1.3 0 0 0 0 2.6zM11.5 13a1.3 1.3 0 1 0 0-2.6 1.3 1.3 0 0 0 0 2.6z'
            }
            fill="none"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
      <span className="dlv-date">{roDate(d.date)}</span>
      <div className="dlv-main">
        <b>{deliveryTitle(d)}</b>
        {line ? <span className="dlv-who">{line}</span> : null}
        {d.note ? <span className="dlv-note">{d.note}</span> : null}
        <DeliveryFiles delivery={d} fileUrl={fileUrl} />
      </div>
      {children ? <div className="acts">{children}</div> : null}
    </li>
  );
}

/**
 * Livrările unei comenzi, cu avizele de însoțire: ce a adus furnizorul, când,
 * cu ce aviz. La chirii, și retururile. Pe pagina comenzii, deasupra foii.
 * Ce a trecut omul de pe șantier așteaptă „Confirmă”.
 */
export function DeliveriesPanel({
  rfqId,
  order,
  rental,
  toast,
  onUpdated,
}: {
  rfqId: string;
  order: PurchaseOrder;
  /** cererea are poziții de închiriere: doar atunci există și retur */
  rental: boolean;
  toast: Toast;
  onUpdated: (r: Rfq) => void;
}) {
  /** null = închis; 'new' = livrare nouă; altfel livrarea de modificat */
  const [editing, setEditing] = useState<Delivery | 'new' | null>(null);
  const list = order.deliveries;
  const canAdd = order.status === 'issued';
  const port = orderPort(rfqId, order.id);
  const waiting = list.filter(toConfirm).length;

  const remove = async (d: Delivery) => {
    if (!(await askRemove(d))) return;
    try {
      onUpdated(await port.remove(d.id));
      toast(t('Livrarea a fost ștearsă'));
    } catch (e) {
      toast(errMsg(e));
    }
  };

  const confirm = async (d: Delivery) => {
    try {
      onUpdated(await api.confirmDelivery(rfqId, order.id, d.id));
      toast(t('Livrarea e confirmată'));
    } catch (e) {
      toast(errMsg(e));
    }
  };

  return (
    <section className="sheet panel deliveries no-print">
      <header className="panel-h">
        <h2>{t('Livrări și avize')}</h2>
        {list.length ? <span className="panel-hint">{list.length}</span> : null}
        {waiting ? (
          <span className="pill wait">
            {waiting === 1 ? t('1 de confirmat') : t('{0} de confirmat', waiting)}
          </span>
        ) : null}
        {canAdd ? (
          <span className="panel-act">
            <button className="btn small" onClick={() => setEditing('new')}>
              {t('Adaugă livrarea')}
            </button>
          </span>
        ) : null}
      </header>

      {list.length ? (
        <ul className="dlv-list">
          {list.map((d) => (
            <DeliveryItem key={d.id} d={d} fileUrl={(f) => port.fileUrl(d.id, f)}>
              {toConfirm(d) ? (
                <button className="btn small primary" onClick={() => void confirm(d)}>
                  {t('Confirmă')}
                </button>
              ) : null}
              <button className="btn small" onClick={() => setEditing(d)}>
                {t('Modifică')}
              </button>
              <button className="btn small" onClick={() => void remove(d)}>
                {t('Șterge')}
              </button>
            </DeliveryItem>
          ))}
        </ul>
      ) : (
        <p className="hint dlv-empty">
          {canAdd
            ? t(
                'La fiecare livrare, trece aici avizul de însoțire: poza sau PDF-ul lui, cu data și numărul. Așa știi mereu ce s-a adus și când. Îl poate trece și șeful de șantier, din contul lui; tu doar îl confirmi.',
              ) + (rental ? t(' Când pleacă înapoi la furnizor, trece la fel și returul.') : '')
            : t('Comanda e anulată; nu s-a trecut nicio livrare pe ea.')}
        </p>
      )}

      {editing ? (
        <DeliveryDialog
          port={port}
          existing={list}
          orderNumber={order.number}
          supplierName={order.supplier.name}
          delivery={editing === 'new' ? null : editing}
          rental={rental}
          note={
            editing !== 'new' && toConfirm(editing)
              ? t('Salvată de tine, livrarea e și confirmată.')
              : undefined
          }
          toast={toast}
          onUpdated={onUpdated}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </section>
  );
}

/** Fișierele avizului, ca legături: pozele și PDF-urile se deschid în browser. */
export function DeliveryFiles({
  delivery: d,
  fileUrl,
}: {
  delivery: Delivery;
  fileUrl: (fileId: string) => string;
}) {
  if (!d.files.length) return <span className="dlv-none">{t('fără fișier')}</span>;
  return (
    <span className="dlv-files">
      {d.files.map((f) => (
        <a key={f.id} href={fileUrl(f.id)} target="_blank" rel="noreferrer">
          {f.filename}
        </a>
      ))}
    </span>
  );
}

export function DeliveryDialog<R>({
  port,
  existing,
  orderNumber,
  supplierName,
  delivery,
  rental,
  note: hintNote,
  toast,
  onUpdated,
  onClose,
}: {
  port: DeliveryPort<R>;
  /** livrările de dinainte: noua e cea care lipsește dintre ele */
  existing: Delivery[];
  orderNumber: string;
  supplierName: string;
  delivery: Delivery | null;
  rental: boolean;
  /** o propoziție în plus sub titlu */
  note?: string;
  toast: Toast;
  onUpdated: (r: R) => void;
  onClose: () => void;
}) {
  const [kind, setKind] = useState<DeliveryKind>(delivery?.kind ?? 'in');
  const [date, setDate] = useState(delivery?.date ?? today());
  const [docNumber, setDocNumber] = useState(delivery?.docNumber ?? '');
  const [note, setNote] = useState(delivery?.note ?? '');
  /** Fișierele alese, care pleacă la „Salvează”. */
  const [pending, setPending] = useState<File[]>([]);
  /** Fișierele deja urcate, la modificare: ștergerea lor se face pe loc. */
  const [files, setFiles] = useState(delivery?.files ?? []);
  const [busy, setBusy] = useState('');

  const fieldsChanged =
    !delivery ||
    kind !== delivery.kind ||
    date !== delivery.date ||
    docNumber.trim() !== delivery.docNumber ||
    note.trim() !== delivery.note;
  const dirty = (delivery ? fieldsChanged : !!(docNumber || note)) || pending.length > 0;

  const pick = (list: FileList | null) => {
    const chosen = [...(list ?? [])];
    const big = chosen.find((f) => f.size > MAX_MB * 1024 * 1024);
    if (big) toast(t('„{0}” are peste {1} MB.', big.name, MAX_MB));
    setPending([...pending, ...chosen.filter((f) => f.size <= MAX_MB * 1024 * 1024)]);
  };

  const removeSaved = async (fileId: string, name: string) => {
    const ok = await ask({
      title: t('Ștergi fișierul?'),
      message: t('„{0}” se șterge de tot.', name),
      confirm: t('Șterge fișierul'),
      danger: true,
    });
    if (!ok || !delivery) return;
    try {
      onUpdated(await port.removeFile(delivery.id, fileId));
      setFiles(files.filter((f) => f.id !== fileId));
    } catch (e) {
      toast(errMsg(e));
    }
  };

  const save = async () => {
    if (!date) return toast(t('Alege data livrării.'));
    const body = { kind, date, docNumber: docNumber.trim(), note: note.trim() };
    let id = delivery?.id;
    let r: R | null = null;
    try {
      setBusy(t('Se salvează…'));
      if (!delivery) {
        r = await port.create(body);
        // Livrarea nouă e cea care nu era înainte pe comandă.
        const before = new Set(existing.map((d) => d.id));
        id = port.deliveriesOf(r).find((d) => !before.has(d.id))?.id;
      } else if (fieldsChanged) {
        r = await port.update(delivery.id, body);
      }
      for (const [i, f] of pending.entries()) {
        setBusy(t('Se urcă {0} din {1}…', i + 1, pending.length));
        r = await port.upload(id!, f);
      }
      if (r) onUpdated(r);
      toast(delivery ? t('Livrarea a fost salvată') : t('Livrarea a fost adăugată'));
      onClose();
    } catch (e) {
      // Ce s-a salvat până la eroare rămâne; arătăm ce e acum pe server.
      if (r) onUpdated(r);
      toast(errMsg(e));
    } finally {
      setBusy('');
    }
  };

  const cancel = () => void confirmDiscard(dirty).then((ok) => ok && onClose());

  return (
    <Dialog onClose={onClose} dirty={dirty}>
      <div className="dlg-h">
        <div>
          <h2>{delivery ? t('Modifică livrarea') : t('Livrare nouă')}</h2>
          <p className="hint">
            {t(
              'Comanda {0}, de la {1}. Datele sunt cele de pe aviz.{2}',
              orderNumber,
              supplierName,
              hintNote ? ` ${hintNote}` : '',
            )}
          </p>
        </div>
      </div>

      <div className="dlg-b pad">
        {/* Retur doar la chirie: la fierul cumpărat ar fi doar zgomot. Un retur trecut deja se vede oricum. */}
        {rental || delivery?.kind === 'return' ? (
          <div className="checks dlv-kind" role="radiogroup" aria-label={t('Ce s-a întâmplat')}>
            <label>
              <input type="radio" checked={kind === 'in'} onChange={() => setKind('in')} />
              <span>
                {t('Livrare')} <span className="muted">{t('· s-a livrat pe șantier')}</span>
              </span>
            </label>
            <label>
              <input type="radio" checked={kind === 'return'} onChange={() => setKind('return')} />
              <span>
                {t('Retur')}
                <span className="muted">{t('· a plecat înapoi la furnizor, la sfârșitul chiriei')}</span>
              </span>
            </label>
          </div>
        ) : null}

        <div className="row dlv-fields">
          <label className="f">
            {t('Data de pe aviz')}
            <DateInput value={date} max={today()} onChange={setDate} />
          </label>
          <label className="f">
            {t('Numărul avizului')}
            <input
              className="inp"
              value={docNumber}
              maxLength={60}
              placeholder={t('opțional')}
              onChange={(e) => setDocNumber(e.target.value)}
            />
          </label>
        </div>

        <label className="f" style={{ marginTop: 'var(--space-4)' }}>
          {t('Observații')}
          <input
            className="inp"
            value={note}
            maxLength={500}
            placeholder={t('lipsesc 2 panouri, livrat parțial')}
            onChange={(e) => setNote(e.target.value)}
          />
          <span className="hint">{t('Doar ce a fost altfel decât pe comandă.')}</span>
        </label>

        <div style={{ marginTop: 'var(--space-4)' }}>
          <span className="f" style={{ marginBottom: 6 }}>
            {t('Avizul')}
          </span>
          {files.length || pending.length ? (
            <div className="docs">
              {files.map((f) => (
                <div className="doc" key={f.id}>
                  <div className="nm">
                    {f.filename}
                    <span>{fmtSize(f.size)}</span>
                  </div>
                  <a href={port.fileUrl(delivery!.id, f.id)} target="_blank" rel="noreferrer">
                    {t('Deschide')}
                  </a>
                  <button
                    className="btn small"
                    disabled={!!busy}
                    onClick={() => void removeSaved(f.id, f.filename)}
                  >
                    {t('Șterge')}
                  </button>
                </div>
              ))}
              {pending.map((f, i) => (
                <div className="doc" key={`${f.name}-${i}`}>
                  <div className="nm">
                    {f.name}
                    <span>{t('{0} · se urcă la salvare', fmtSize(f.size))}</span>
                  </div>
                  <button
                    className="btn small"
                    disabled={!!busy}
                    onClick={() => setPending(pending.filter((_, j) => j !== i))}
                  >
                    {t('Scoate')}
                  </button>
                </div>
              ))}
            </div>
          ) : null}
          <div className="drop">
            <span>{t('Poza avizului făcută cu telefonul sau PDF-ul primit')}</span>
            <label className="btn small">
              {t('Alege fișiere')}
              <input
                type="file"
                accept={ACCEPT}
                multiple
                hidden
                onChange={(e) => {
                  pick(e.target.files);
                  e.target.value = '';
                }}
              />
            </label>
          </div>
        </div>
      </div>

      <div className="dlg-f">
        <button className="btn" disabled={!!busy} onClick={cancel}>
          {t('Anulează')}
        </button>
        <button className="btn primary" disabled={!!busy} onClick={() => void save()}>
          {busy || t('Salvează')}
        </button>
      </div>
    </Dialog>
  );
}
